import type { AnswerGroup, TextSource } from '../../types';
import type { MetricFormat } from '../../definition/types';

export enum ColumnType {
  CUSTOM_METRIC = 'customMetric',
  QUESTION_ANSWER_COUNT = 'questionAnswerCount',
  LABEL = 'label',
  TEAM = 'team',
  RESPONDENT_COUNT = 'respondentCount',
  ANSWER_COUNT = 'answerCount',
  RESPONSE_RATE = 'responseRate',
  NPS = 'nps',
  MINI_NPS = 'miniNps',
  SCORE = 'score',
  CHOICES = 'choices',
  TEXT = 'text',
  THEMES = 'themes',
}

export type MetricCellPresentation = 'nps' | 'miniNps' | 'score' | 'percentage' | 'number' | 'distribution';

type ColumnBase = {
  /** Stable identity; labels and question indexes are not necessarily unique. */
  id: string;
  label: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
};
type QuestionBinding = { questionIndex: string; statementIndex?: number };

export type TableColumn = ColumnBase &
  (
    | {
        type:
          | ColumnType.LABEL
          | ColumnType.TEAM
          | ColumnType.RESPONDENT_COUNT
          | ColumnType.ANSWER_COUNT
          | ColumnType.RESPONSE_RATE;
      }
    | {
        type: ColumnType.CUSTOM_METRIC;
        metricId: string;
        presentation?: MetricCellPresentation;
        format?: MetricFormat;
      }
    | (QuestionBinding & {
        type:
          | ColumnType.NPS
          | ColumnType.MINI_NPS
          | ColumnType.SCORE
          | ColumnType.CHOICES
          | ColumnType.QUESTION_ANSWER_COUNT;
      })
    | (QuestionBinding & { type: ColumnType.TEXT | ColumnType.THEMES; source?: TextSource })
  );

export interface AnswerGroupTableProps {
  groups: AnswerGroup[];
  columns: TableColumn[];
  pageSize?: number;
  columnResizeEnabled?: boolean;
  horizontalScroll?: boolean;
  /** Single-column header sorting within each sibling list. Defaults to false. */
  sortingEnabled?: boolean;
  /** One text search with an optional column scope. Does not recalculate summaries. */
  searchEnabled?: boolean;
  /** Pointer drag handles for column ordering. Defaults to false. */
  columnOrderingEnabled?: boolean;
  /** Optional controlled order; omitted/unknown IDs are reconciled with columns. */
  columnOrder?: string[];
  onColumnOrderChange?: (columnIds: string[]) => void;
  /** Authoring-only resize commits; viewport fitting never changes the saved definition. */
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  /** Reset row expansion/page on a semantic report/view change, without discarding widths. */
  resetKey?: string;
  label: string;
}
